/**
 * Veelgestelde vragen per persona (stap 4, 03-11-2026). De actuele teksten staan in de database (public.faq_items,
 * beheerd in admin.antreja.nl › Hulpteksten); deze lijst is de startinhoud en de terugval zonder verbinding.
 * Schrijfwijze: algemeen voor elke sport (club, vereniging, team), geen voetbaltermen; korte zinnen; knoppen en
 * menu's precies zoals ze in de app en het portaal heten.
 */

export type FaqPersona = 'lid' | 'groepsbeheerder' | 'clubbeheerder' | 'installateur';
export type FaqSurface = 'app' | 'portaal';

export interface FaqItem {
  id: string;
  persona: FaqPersona;
  /** Waar de vraag getoond wordt. */
  surfaces: readonly FaqSurface[];
  q: string;
  /** Alinea's; een regel die met "• " begint wordt als opsommingsteken getoond. */
  a: readonly string[];
}

export const FAQ_PERSONA_LABEL: Readonly<Record<FaqPersona, string>> = {
  lid: 'Deuren openen',
  groepsbeheerder: 'Groepsbeheerder',
  clubbeheerder: 'Clubbeheerder',
  installateur: 'Installateur',
};

export const FAQ_PERSONA_INTRO: Readonly<Record<FaqPersona, string>> = {
  lid: 'Voor iedereen die met de Antreja-app, een pas of een code naar binnen gaat.',
  groepsbeheerder: 'Voor wie een eigen groep bijhoudt, zoals een coördinator, teammanager of wedstrijdsecretaris.',
  clubbeheerder: 'Voor wie het beheer van de club doet: deuren, groepen, tijden en beheerders.',
  installateur: 'Voor wie sloten installeert, reset en passen inleert.',
};

const BOTH = ['app', 'portaal'] as const;
const APP = ['app'] as const;
const PORTAAL = ['portaal'] as const;

export const FAQ: readonly FaqItem[] = [
  // ---------------------------------------------------------------- lid / vrijwilliger
  {
    id: 'lid-openen',
    persona: 'lid',
    surfaces: BOTH,
    q: 'Hoe open ik een deur met de app?',
    a: [
      'Ga naar Mijn deuren en houd de knop Open deur ingedrukt tot de deur opengaat.',
      'Sta dicht bij de deur (een paar meter) en zet Bluetooth aan. De app praat via Bluetooth met het slot.',
    ],
  },
  {
    id: 'lid-geen-deur',
    persona: 'lid',
    surfaces: BOTH,
    q: 'Een deur staat niet in mijn lijst. Hoe kan dat?',
    a: [
      'Je krijgt toegang via een groep van de club, bijvoorbeeld "Trainers" of "Kantine". Staat een deur er niet bij, dan zit je (nog) niet in een groep die daar mag komen.',
      'Vraag het aan de beheerder van je groep of aan de clubbeheerder.',
    ],
  },
  {
    id: 'lid-tijden',
    persona: 'lid',
    surfaces: APP,
    q: 'Op welke dagen en tijden mag ik naar binnen?',
    a: ['Onder Mijn toegang zie je per deur de dagen en tijden die voor jou gelden. Buiten die tijden gaat de deur niet open.'],
  },
  {
    id: 'lid-klaargezet',
    persona: 'lid',
    surfaces: APP,
    q: 'Er staat "Je toegang wordt nog klaargezet". Wat betekent dat?',
    a: [
      'De club heeft je toegang gegeven, maar die staat nog niet op het slot. Dat gebeurt als een clubbeheerder of installateur met de app bij die deur is geweest.',
      'Duurt het lang? Laat het de clubbeheerder weten.',
    ],
  },
  {
    id: 'lid-inloggen',
    persona: 'lid',
    surfaces: APP,
    q: 'Hoe log ik in? Ik heb geen wachtwoord.',
    a: [
      'Dat klopt: je logt in met je e-mailadres. Je krijgt dan een code per mail die je in de app invult.',
      'Gebruik het e-mailadres waarop je de uitnodiging van de club kreeg.',
    ],
  },
  {
    id: 'lid-nieuwe-telefoon',
    persona: 'lid',
    surfaces: APP,
    q: 'Ik heb een nieuwe telefoon. Wat moet ik doen?',
    a: ['Installeer de Antreja-app en log in met hetzelfde e-mailadres. Je deuren staan er dan weer in.'],
  },
  {
    id: 'lid-internet',
    persona: 'lid',
    surfaces: APP,
    q: 'Heb ik internet nodig om een deur te openen?',
    a: [
      'Het openen zelf gaat via Bluetooth. Internet is nodig om je toegang bij te werken en om te melden wat er op het slot is gebeurd. Gegevens die nog verstuurd moeten worden, gaan vanzelf zodra je weer online bent.',
    ],
  },
  {
    id: 'lid-pas-kwijt',
    persona: 'lid',
    surfaces: BOTH,
    q: 'Ik ben mijn pas kwijt. Wat nu?',
    a: [
      'Meld het meteen bij de clubbeheerder. Die blokkeert de pas.',
      'Het slot weigert de pas zodra een clubbeheerder of installateur met de Antreja-app bij die deur is geweest. Tot dat moment kan de pas nog werken; meld het dus zo snel mogelijk.',
    ],
  },
  {
    id: 'lid-vanzelf-op-slot',
    persona: 'lid',
    surfaces: APP,
    q: 'Gaat de deur vanzelf weer op slot?',
    a: ['Ja, meestal na een paar seconden. De club stelt per deur in na hoeveel tijd, of dat het uit staat.'],
  },
  {
    id: 'lid-privacy',
    persona: 'lid',
    surfaces: BOTH,
    q: 'Wat ziet de club van mij?',
    a: [
      'De club ziet welke deur wanneer door wie is geopend (het logboek). Dat wordt na een vaste termijn automatisch verwijderd; de club bepaalt hoe lang, tussen 30 en 730 dagen.',
      'Je locatie wordt niet bijgehouden. Bluetooth wordt alleen gebruikt om met het slot te praten.',
    ],
  },
  {
    id: 'lid-gast',
    persona: 'lid',
    surfaces: BOTH,
    q: 'Hoe komt een gast binnen, zoals een bezoekend team of een monteur?',
    a: [
      'Een gast heeft geen app nodig. De club kan een tijdelijke code maken (op deuren met een cijferpaneel) of een uitleenpas meegeven.',
      'Vraag het aan de beheerder van je groep of aan de clubbeheerder.',
    ],
  },

  // ---------------------------------------------------------------- groepsbeheerder
  {
    id: 'gb-wat',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Wat kan ik als groepsbeheerder?',
    a: [
      '• Personen aan je eigen groep(en) toevoegen en eruit halen.',
      '• Leden van je groep uitnodigen voor de app.',
      '• Tijdelijke codes maken voor deuren van je groep, tot 7 dagen.',
      '• Uitleenpassen van je groep uitlenen en innemen.',
      '• Het logboek van je groep bekijken.',
      'Deuren, dagen en tijden van de groep stelt de clubbeheerder in.',
    ],
  },
  {
    id: 'gb-toevoegen',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe geef ik iemand toegang?',
    a: [
      'Ga naar Groepen, open je groep en kies Lid toevoegen. Staat de persoon er nog niet in? Kies dan eerst Persoon toevoegen op de pagina Personen.',
      'Wil die persoon met de app openen? Open de persoon en kies Uitnodigen voor de app.',
    ],
  },
  {
    id: 'gb-stopt',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Iemand stopt. Hoe haal ik de toegang weg?',
    a: [
      'Open de persoon en kies bij de groep Uit groep halen. Zijn passen en codes voor die deuren worden dan ook ingetrokken.',
      'Op het slot is de intrekking actief zodra een clubbeheerder of installateur met de Antreja-app bij de deur is geweest (Toegang klaarzetten). Is het dringend? Laat het de clubbeheerder weten.',
    ],
  },
  {
    id: 'gb-code',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe maak ik een tijdelijke code voor een gast?',
    a: [
      'Ga naar Codes en kies Code maken. Kies de deur, de periode (maximaal 7 dagen) en voor wie de code is.',
      'Codes werken alleen op deuren met een cijferpaneel. De code zie je één keer; geef hem meteen door.',
    ],
  },
  {
    id: 'gb-uitleenpas',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe leen ik een uitleenpas uit?',
    a: [
      'Ga naar Uitleenpassen en kies Uitlenen bij een pas die Beschikbaar is. Vul de naam van de gast, de vereniging of het team en de inleverdatum in.',
      'Krijg je de pas terug? Kies Innemen.',
      'De pas opent alleen de deuren en tijden van zijn groep, ook als hij te laat terugkomt. Komt hij niet terug, vraag de clubbeheerder dan om de pas te blokkeren.',
    ],
  },
  {
    id: 'gb-uitnodigen',
    persona: 'groepsbeheerder',
    surfaces: PORTAAL,
    q: 'Waarom kan ik iemand niet op een ander e-mailadres uitnodigen?',
    a: [
      'Om misbruik te voorkomen nodig je als groepsbeheerder alleen uit op het e-mailadres dat al bij de persoon bekend is. Moet het adres anders? Vraag het de clubbeheerder.',
    ],
  },

  // ---------------------------------------------------------------- clubbeheerder
  {
    id: 'cb-tweestaps',
    persona: 'clubbeheerder',
    surfaces: BOTH,
    q: 'Waarom moet ik een code uit een authenticator-app invullen?',
    a: [
      'Als clubbeheerder kun je bepalen wie waar naar binnen mag. Daarom vraagt Antreja naast je e-mailcode ook een code uit een authenticator-app, zoals Google Authenticator of Microsoft Authenticator. Dat heet tweestapsverificatie.',
      'Je stelt het één keer in, in het portaal en in de app. Daarna vul je bij het inloggen de code in die de authenticator-app op dat moment laat zien.',
    ],
  },
  {
    id: 'cb-toegang',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe regel ik wie waar naar binnen mag?',
    a: [
      'Toegang loopt altijd via een groep:',
      '• Maak bij Groepen een groep, bijvoorbeeld "Kantine" of "Trainers".',
      '• Leg in de groep vast welke deuren op welke dagen en tijden.',
      '• Voeg personen toe aan de groep.',
      'Iemand die in meer groepen zit, krijgt alles bij elkaar.',
    ],
  },
  {
    id: 'cb-op-slot',
    persona: 'clubbeheerder',
    surfaces: BOTH,
    q: 'Wanneer staat een wijziging op het slot?',
    a: [
      'De sloten hebben geen eigen internetverbinding. Een wijziging komt op het slot via een telefoon met de Antreja-app bij de deur.',
      '• Nieuwe toegang en iemand uit een groep halen: open in de app Mijn deuren en kies Toegang klaarzetten als je bij de deur bent.',
      '• Een geblokkeerde pas: die gaat mee met de app van een clubbeheerder of installateur bij de deur.',
      '• Een ingetrokken code: die gaat ook mee met de app van een gewoon lid dat langs de deur komt.',
      'Bij Instellingen stel je in na hoeveel uur je een melding krijgt als een intrekking nog niet op het slot staat.',
    ],
  },
  {
    id: 'cb-installateur',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe wijs ik een installateur aan?',
    a: [
      'Open de persoon bij Personen en kies bij Installateur Aanwijzen als installateur. Kies een einddatum (maximaal een jaar). De persoon moet de Antreja-app al hebben.',
      'Een installateur kan met de app sloten koppelen, resetten en passen inleren bij jullie deuren. Bij Instellingen zie je wie installateur is.',
    ],
  },
  {
    id: 'cb-beheerders',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe maak ik iemand clubbeheerder of groepsbeheerder?',
    a: ['Ga naar Instellingen, onderdeel Beheerders, en kies Beheerder uitnodigen. Kies daar de rol en bij een groepsbeheerder de groep(en).'],
  },
  {
    id: 'cb-uitleenpas',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe zet ik uitleenpassen voor gasten op?',
    a: [
      '• Maak een groep voor gasten met de deuren en tijden waarop gasten binnen mogen.',
      '• Ga naar Uitleenpassen en kies Nieuwe uitleenpas. Geef de pas een naam die ook op de pas zelf staat.',
      '• Laat de installateur de pas inleren in de app (stap Pas of vinger inleren).',
      '• Maak de wedstrijdsecretaris of een andere vrijwilliger groepsbeheerder van die groep. Die kan dan uitlenen en innemen.',
    ],
  },
  {
    id: 'cb-blokkeren',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe blokkeer ik een pas die kwijt is?',
    a: [
      'Open de persoon (of de uitleenpas) en kies bij de pas Pas blokkeren.',
      'Het slot weigert de pas zodra jij of een installateur met de Antreja-app bij de deur bent geweest. Wil je het meteen? Ga met de app naar de deur.',
    ],
  },
  {
    id: 'cb-auto-lock',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Kan een deur vanzelf op slot gaan?',
    a: [
      'Ja. Standaard gaat een deur na 5 seconden weer op slot. Bij Deuren stel je per deur een andere tijd in (van 3 seconden tot 30 minuten) of zet je het uit.',
      'De nieuwe instelling komt op het slot als iemand met de app bij de deur is.',
    ],
  },
  {
    id: 'cb-batterij',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe weet ik dat een batterij bijna leeg is?',
    a: [
      'Je krijgt een e-mail als de batterij van een slot onder het percentage komt dat je bij Instellingen hebt gekozen. Bij Deuren zie je de stand per slot.',
      'Batterijen vervangen? Kies bij de deur Batterijen vervangen, dan staat de stand weer op 100%.',
    ],
  },
  {
    id: 'cb-reset',
    persona: 'clubbeheerder',
    surfaces: BOTH,
    q: 'Hoe zet ik een slot terug naar de fabrieksinstellingen?',
    a: [
      'Dat gaat met de app, terwijl je bij het slot staat: Profiel, Sloten van de club, Slot resetten. Je bevestigt eerst met je authenticator-app.',
      'Daarna is het slot leeg en kan het opnieuw gekoppeld worden.',
    ],
  },
  {
    id: 'cb-import',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Kan ik leden importeren uit onze ledenadministratie?',
    a: ['Ja. Exporteer een CSV-bestand uit je ledenadministratie en kies Import. Antreja herkent de kolommen; je ziet eerst wat er nieuw is en wat wordt bijgewerkt.'],
  },
  {
    id: 'cb-bewaren',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Hoe lang wordt het logboek bewaard?',
    a: [
      'Dat bepaal je zelf bij Instellingen, Logboek bewaren (dagen): tussen 30 en 730 dagen. Oudere regels verwijdert Antreja automatisch.',
    ],
  },
  {
    id: 'cb-minderjarig',
    persona: 'clubbeheerder',
    surfaces: PORTAAL,
    q: 'Wat geldt er voor minderjarigen?',
    a: [
      'Minderjarigen kunnen een pas, een code of de app gebruiken. Een vingerafdruk wordt bij minderjarigen niet ingeleerd.',
      'Bij een uitnodiging voor de app van een minderjarige bevestig je dat de ouder of verzorger is geïnformeerd.',
    ],
  },

  // ---------------------------------------------------------------- installateur
  {
    id: 'inst-stappen',
    persona: 'installateur',
    surfaces: BOTH,
    q: 'Wanneer is een deur klaar?',
    a: [
      'Na twee stappen in de app: Koppelen en de Checklist. Daarna opent iedereen die via zijn groep toegang heeft de deur met de Antreja-app.',
      'Een pas of vingerafdruk inleren is optioneel; dat doe je alleen als de club dat wil.',
    ],
  },
  {
    id: 'inst-tweestaps',
    persona: 'installateur',
    surfaces: APP,
    q: 'Waarom vraagt de app om een authenticator-app?',
    a: [
      'Installeren, resetten en toegang klaarzetten zijn beheerhandelingen. Daarom bevestig je met een code uit een authenticator-app (bijvoorbeeld Google Authenticator of Microsoft Authenticator).',
      'De eerste keer stel je dat in: tik op Instellen en daarna op Open in authenticator-app. Vul daarna de 6 cijfers in.',
    ],
  },
  {
    id: 'inst-niet-toegewezen',
    persona: 'installateur',
    surfaces: APP,
    q: 'De app zegt dat ik nog niet als installateur ben toegewezen.',
    a: [
      'Je mag alleen installeren bij een club die jou heeft aangewezen. Vraag de clubbeheerder om je in het portaal als installateur aan te wijzen. Een toewijzing heeft een einddatum; is die voorbij, vraag dan om verlenging.',
    ],
  },
  {
    id: 'inst-niet-gevonden',
    persona: 'installateur',
    surfaces: APP,
    q: 'De app vindt het slot niet.',
    a: [
      '• Zet Bluetooth aan en geef de app toestemming voor apparaten in de buurt.',
      '• Houd de telefoon binnen 1 meter van het slot.',
      '• Maak het slot wakker: raak het even aan (sensor, klavier of pas).',
      '• Controleer of het slot in de fabrieksstand staat. Is het al ergens anders gekoppeld, reset het dan eerst.',
    ],
  },
  {
    id: 'inst-inleren',
    persona: 'installateur',
    surfaces: APP,
    q: 'Voor wie kan ik een pas of vingerafdruk inleren?',
    a: [
      'Alleen voor personen die via een groep toegang hebben tot die deur. Staat iemand niet in de lijst? Laat de clubbeheerder hem eerst aan een groep toevoegen.',
      'Bij minderjarigen leer je geen vingerafdruk in. Bij volwassenen vraag je eerst toestemming en vink je die aan.',
    ],
  },
  {
    id: 'inst-uitleenpas',
    persona: 'installateur',
    surfaces: APP,
    q: 'Hoe leer ik een uitleenpas in?',
    a: [
      'Uitleenpassen staan bovenaan de lijst bij Pas of vinger inleren. Kies de uitleenpas en houd de pas van de club tegen het slot. Bij een uitleenpas kun je alleen een pas inleren.',
    ],
  },
  {
    id: 'inst-reset',
    persona: 'installateur',
    surfaces: APP,
    q: 'Hoe reset ik een slot?',
    a: [
      'Ga bij het slot staan en kies Slot resetten (onder Installeren, of op Profiel bij Sloten van de club). Na de reset is het slot leeg en koppel je het opnieuw.',
      'Ingeleerde passen en vingerafdrukken zijn na een reset weg en moeten opnieuw worden ingeleerd.',
    ],
  },
];

/** Rij uit de tabel public.faq_items (beheerd in admin.antreja.nl). */
export interface FaqRow {
  id: string;
  persona: FaqPersona;
  surfaces: FaqSurface[];
  question: string;
  answer: string;
  sort_order: number;
  published: boolean;
  updated_at?: string;
}

export const FAQ_PERSONAS: readonly FaqPersona[] = ['lid', 'groepsbeheerder', 'clubbeheerder', 'installateur'];

/** Antwoordtekst → alinea's; "- " of "* " aan het begin wordt een opsommingsteken. */
export function answerLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (/^[-*•]\s+/.test(l) ? `• ${l.replace(/^[-*•]\s+/, '')}` : l));
}

/** Database-rijen → vragen (alleen gepubliceerd, op volgorde). */
export function faqFromRows(rows: readonly FaqRow[]): FaqItem[] {
  return [...rows]
    .filter((r) => r.published)
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))
    .map((r) => ({ id: r.id, persona: r.persona, surfaces: r.surfaces, q: r.question, a: answerLines(r.answer) }));
}

/** Ingebouwde teksten als rijen (demo en terugval). */
export function faqRowsFromBuiltIn(): FaqRow[] {
  return FAQ.map((f, i) => ({ id: f.id, persona: f.persona, surfaces: [...f.surfaces], question: f.q, answer: f.a.join('\n'), sort_order: (i + 1) * 10, published: true }));
}

/** Vragen voor een scherm en een set persona's, in volgorde. Standaard de ingebouwde teksten. */
export function faqFor(surface: FaqSurface, personas: readonly FaqPersona[], items: readonly FaqItem[] = FAQ): FaqItem[] {
  return items.filter((f) => f.surfaces.includes(surface) && personas.includes(f.persona));
}

/** Eenvoudig zoeken in vraag en antwoord (hoofdletterongevoelig). */
export function faqSearch(items: readonly FaqItem[], query: string): FaqItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  return items.filter((f) => f.q.toLowerCase().includes(q) || f.a.some((p) => p.toLowerCase().includes(q)));
}
